import { useMemo, useState } from 'react'

import type {
  CMSCollectionFilter,
  CMSCollectionFilterGroup,
  CMSCollectionGridItem,
} from '@/types/sanity'
import { filterGridItems, getAvailableFilters } from '@/utilities/collection'

export const useCollectionFilters = ({
  filters,
  gridItems,
  appliedFiltersDefault = {},
}: {
  filters: CMSCollectionFilterGroup[]
  gridItems: CMSCollectionGridItem[]
  appliedFiltersDefault?: Record<string, CMSCollectionFilter>
}) => {
  const [appliedFilters, setAppliedFilters] = useState<
    Record<string, CMSCollectionFilter>
  >(appliedFiltersDefault)

  const filteredGridItems = useMemo(() => {
    return filterGridItems({ gridItems, appliedFilters })
  }, [gridItems, appliedFilters])

  const availableFilters = useMemo<
    Record<string, CMSCollectionFilter[]>
  >(() => {
    return getAvailableFilters({
      gridItems,
      filters,
      appliedFilters,
    })
  }, [filters, appliedFilters])

  const isFilterApplied = ({
    filter,
    filterGroup,
  }: {
    filter: CMSCollectionFilter
    filterGroup: CMSCollectionFilterGroup
  }) => {
    return appliedFilters[filterGroup._id]?._id === filter._id
  }

  const isFilterAvailable = ({
    filter,
    filterGroup,
  }: {
    filter: CMSCollectionFilter
    filterGroup: CMSCollectionFilterGroup
  }) => {
    return (
      availableFilters[filterGroup._id]?.some(
        ({ label }) => label === filter.label,
      ) || isFilterApplied({ filter, filterGroup })
    )
  }

  const hasFilterGroupAppliedFilter = (
    filterGroup: CMSCollectionFilterGroup,
  ) => {
    return !!appliedFilters[filterGroup._id]
  }

  const handleFilterChange = ({
    filter,
    filterGroup,
  }: {
    filter?: CMSCollectionFilter
    filterGroup: CMSCollectionFilterGroup
  }) => {
    const filterGroupId = filterGroup._id
    const valueToSet =
      appliedFilters[filterGroupId]?._id === filter?._id ? undefined : filter

    if (valueToSet) {
      return setAppliedFilters({
        ...appliedFilters,
        [filterGroupId]: valueToSet,
      })
    } else {
      const { [filterGroupId]: _, ...rest } = appliedFilters
      return setAppliedFilters(rest)
    }
  }

  return {
    appliedFilters,
    setAppliedFilters,
    filteredGridItems,
    availableFilters,
    isFilterApplied,
    isFilterAvailable,
    hasFilterGroupAppliedFilter,
    handleFilterChange,
  }
}
